'use client'

import { useRef } from 'react'

import type { TextValue } from '@/lib/content-standard/rich-text'
import { type DisplayCurrency } from '@/lib/currency/map'
import { cn } from '@/lib/utils'
import { CrawlSafeLink } from '@/components/crawl-safe-link'

import { DropdownCaret } from './dropdown-caret'
import { useDismissOnOutsidePress } from './use-dismiss-on-outside-press'

/** One currency as the picker offers it — resolved by the caller, like the locale menu's. */
export interface CurrencyOption {
    currency: DisplayCurrency
    /** The currency's name and symbol in the page's locale, as the server rendered them. */
    name: string
    symbol: string
    /** The address that renders the current page in this currency. */
    url: string
    /** Whether the page's URL selects it. */
    isActive: boolean
}

/** Row geometry, shared between the panel's options and the vertical mirror. */
const ROW_CLASS = 'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm'
const ROW_HOVER =
    'transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none'

/** The symbol column, fixed-width so the names align down a column. */
const SYMBOL_CLASS = 'w-10 shrink-0 font-bold'

interface CurrencyRowProps {
    option: CurrencyOption
}

/**
 * One currency in the panel.
 *
 * **A `CrawlSafeLink`, not an anchor.** Every option addresses the current page in
 * another currency — a URL that declares the param-free page as its canonical, so it is
 * deliberately never indexed. Rendering it as an `href` would be the only thing on the
 * site that puts such a URL in the served HTML: nothing else emits one, because a link
 * carries a currency only on a page that already has one, and a crawler arrives without.
 * That makes this control the single doorway from the canonical graph into the currency
 * variants, and an anchor here would open every route's ×15 to crawling for no ranking
 * benefit (`invariants.md#no-transient-search-links` is the same reasoning on a
 * never-indexed destination).
 */
function CurrencyRow({ option }: CurrencyRowProps) {
    return (
        <CrawlSafeLink
            url={option.url}
            // This option's address *is* the currency decision — the page's own selection
            // must not be stamped back onto it, or the locale default becomes unreachable.
            currencyIsAuthored
            className={cn(ROW_CLASS, ROW_HOVER, option.isActive && 'font-semibold text-foreground')}
            testId={`currency-option-${option.currency}`}
        >
            <span className={SYMBOL_CLASS} aria-hidden="true">
                {option.symbol}
            </span>
            {option.name}
        </CrawlSafeLink>
    )
}

interface CurrencyMenuProps {
    /** The currencies to offer, in the order they are listed. */
    options: CurrencyOption[]
}

interface CurrencyMenuTriggerProps extends CurrencyMenuProps {
    /** The picker's purpose, which its accessible name leads with. */
    label: TextValue
}

/**
 * The header's currency picker: the currency the page's address selects, opening a list
 * of the rest.
 *
 * A native `<details>` for the same reasons the country picker uses one — click and
 * keyboard toggling, an announced expanded state, and the chevron rotation, with no
 * controlled state and none of the hover/press phase trap a hover-open listener invites.
 * Unlike that picker there is no crawlability motive, since the options carry no `href`
 * at all; what `<details>` buys here is the interaction, and `useDismissOnOutsidePress`
 * supplies the close-on-Escape/outside-press the element lacks.
 *
 * Presentational and pathname-blind: the caller resolves each option's address, the same
 * way `AppLayout` builds the menu, logo and locale configuration.
 */
export function CurrencyMenu({ options, label }: CurrencyMenuTriggerProps) {
    const detailsRef = useRef<HTMLDetailsElement>(null)

    useDismissOnOutsidePress(detailsRef)

    const active = options.find((option) => option.isActive)

    // An absent active option would leave the trigger blank, and fewer than two
    // currencies is not a choice. Either way the header shows no picker rather than a
    // dead one.
    if (!active || options.length < 2) {
        return null
    }

    return (
        <details ref={detailsRef} className="group/currency relative">
            <summary
                // The purpose AND the current currency, in one label: an `aria-label`
                // replaces name-from-content, so a visually-hidden name inside the
                // control would never be announced.
                aria-label={`${label.text} — ${active.name}`}
                className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-2 font-bold outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden"
            >
                <span aria-hidden="true">{active.symbol}</span>
                <DropdownCaret className="transition duration-300 group-open/currency:rotate-180" />
            </summary>
            {/* Three columns filled top-to-bottom, as ui-react's `MultiColumnMenu` lays
                them out: `grid-flow-col` keeps the DOM in the declared order while the
                eye runs down each column. Five rows holds the fifteen currencies in
                three columns; a sixteenth becomes a fourth column and the panel widens
                to hold it. */}
            <div className="absolute top-full right-0 z-50 mt-1.5 w-max max-w-[calc(100vw-2rem)] min-w-[34rem] rounded-md border bg-popover p-2 text-popover-foreground shadow">
                <ul className="grid list-none auto-cols-fr grid-flow-col grid-rows-5 gap-0.5">
                    {options.map((option) => (
                        <li key={option.currency}>
                            <CurrencyRow option={option} />
                        </li>
                    ))}
                </ul>
            </div>
        </details>
    )
}

/**
 * The sidebar's vertical mirror, for the viewports the header picker is hidden at.
 * Same options and same controls, laid out in one column.
 */
export function CurrencyMenuVertical({ options }: CurrencyMenuProps) {
    const active = options.find((option) => option.isActive)

    if (!active || options.length < 2) {
        return null
    }

    return (
        <ul className="grid list-none gap-0.5">
            {options.map((option) => (
                <li key={option.currency}>
                    <CurrencyRow option={option} />
                </li>
            ))}
        </ul>
    )
}
